import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { Label } from '@mentimeter/slides-presentation-ui/title/label';
import React from 'react';
import { alpha } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import type { QuoteStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getNinePointLayoutProperties } from '../../shared/utils/getNinePointLayoutProperties';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { getLegacyLayout } from '../../utils/legacyContentSlideUtils/getLegacyLayout';

export function QuotePresentation() {
  const {
    Utils: { fontOffset },
    useConfig,
  } = usePresentationContext();

  const slide = usePresentationSlideContext();
  const staticContent = slide.staticContent as QuoteStaticContent;
  const title = staticContent.quote;
  const fontSizeOffset = staticContent.textSizeOffset;
  const legacyLayout = getLegacyLayout(slide) ?? {
    type: 'default',
    font_size_offset: 0,
    alignment_x: 1,
    alignment_y: 1,
    size: '25%',
  };
  const { enableAnimations } = useConfig();

  // for font-size calculations
  const breakpoints = [
    { lineCount: 8, offset: -4 },
    { lineCount: 6, offset: -2 },
    { lineCount: 4, offset: -1 },
  ];
  const layoutType = legacyLayout?.type ?? 'default';
  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(legacyLayout);
  // quote font size
  const width = layoutType === 'default' || layoutType === 'full-bg' ? 30 : 20;
  const height = Math.ceil(title.length / width);
  const offset =
    fontOffset({ height, width, breakpoints }) + (fontSizeOffset ?? 0);

  return (
    <Box
      maxWidth="85%"
      height="100%"
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      <StaggerCss index={1} height="auto" enableAnimations={enableAnimations}>
        {staticContent.label && (
          <Label>
            <MarkdownText
              disableLineBreaks
              source={staticContent.label}
              allowHyperlinks
            />
          </Label>
        )}
        <Box
          as="blockquote"
          width="100%"
          alignItems={alignItems}
          justifyContent={justifyContent}
          mt={5}
        >
          <Text
            aria-hidden
            fontSize="600px"
            fontFamily="heading"
            fontWeight="semiBold"
            color="text"
            lineHeight="none"
            mb="-500px"
            mt="-120px"
            extend={({ theme }) => ({
              color: alpha(theme.colors.primary, 0.25),
              fontFamily: 'arial',
            })}
          >
            &ldquo;
          </Text>
          <FlexibleText
            as="p"
            fontWeight="light"
            fontOffset={offset}
            fontSize={13}
            fontFamily="heading"
            lineHeight="heading"
            textAlign={textAlign}
            color="text"
          >
            <MarkdownText
              source={title}
              disableLineBreaks={false}
              allowBlankLines
              allowHyperlinks
            />
          </FlexibleText>
        </Box>
      </StaggerCss>
      {staticContent.author && (
        <StaggerCss index={2} height="auto" enableAnimations={enableAnimations}>
          <Box
            width="100%"
            alignItems={alignItems}
            justifyContent={justifyContent}
          >
            <Text
              as="p"
              color="text"
              fontSize="225"
              fontStyle="italic"
              mb="-50px"
              mt={5}
              textAlign={textAlign}
            >
              <MarkdownText
                source={`– ${staticContent.author}`}
                disableLineBreaks
                allowHyperlinks
              />
            </Text>
          </Box>
        </StaggerCss>
      )}
    </Box>
  );
}
